import React, { useCallback, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
    X, UploadCloud, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, Copy, Loader2, RefreshCw,
    UserPlus, UserCheck, SkipForward, ArrowRight, Tag, Info,
} from 'lucide-react';
import styles from './BulkImportClients.module.css';
import { SMIIFY_API } from '../../../config/api';

// =============================================================================
// Import many clients from Excel (.xlsx / .xls) or CSV.
// Upload -> match columns -> check rows -> import in parts -> results (+ Excel of problem rows)
// The server checks every row again; money fields are never touched by an import.
// =============================================================================
const CHUNK = 300;                 // rows per request (the server takes up to 1000)
const MAX_FILE_MB = 50;            // any number of clients; only the file size is capped
const RETRIES = 3;                 // a part that fails is tried again before stopping
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const FIELDS = [
    { key: 'name', label: 'Full name', hint: 'or First + Last name' },
    { key: 'firstName', label: 'First name' },
    { key: 'lastName', label: 'Last name' },
    { key: 'phone', label: 'Mobile', required: true },
    { key: 'email', label: 'Email' },
    { key: 'gender', label: 'Gender' },
    { key: 'dob', label: 'Birthday' },
    { key: 'anniversary', label: 'Anniversary' },
    { key: 'address', label: 'Address / area' },
    { key: 'notes', label: 'Notes' },
    { key: 'tags', label: 'Tags / group' },
];
const SYNONYMS = {
    name: ['name', 'full name', 'fullname', 'client name', 'customer name', 'client', 'customer', 'guest name', 'naam'],
    firstName: ['first name', 'firstname', 'fname', 'given name'],
    lastName: ['last name', 'lastname', 'surname', 'lname', 'family name'],
    phone: ['mobile', 'mobile number', 'mobile no', 'phone', 'phone number', 'phone no', 'contact', 'contact number', 'contact no', 'whatsapp', 'whatsapp number', 'mob', 'mob no', 'cell', 'number'],
    email: ['email', 'e mail', 'email id', 'email address', 'mail'],
    gender: ['gender', 'sex'],
    dob: ['birthday', 'dob', 'date of birth', 'birth date', 'birthdate', 'bday'],
    anniversary: ['anniversary', 'anniversary date', 'marriage anniversary', 'wedding anniversary'],
    address: ['address', 'area', 'location', 'locality', 'city'],
    notes: ['notes', 'note', 'remarks', 'remark', 'comments', 'comment'],
    tags: ['tags', 'tag', 'group', 'segment', 'category', 'type'],
};
const normHeader = (h) => String(h ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** +91 98765 43210 / 09876543210 / 9.8765E+09 -> 9876543210 (same rule as the server) */
const cleanMobile = (raw) => {
    let s = typeof raw === 'number' ? Math.round(raw).toString() : String(raw ?? '').trim();
    if (/^\d+(\.\d+)?e\+?\d+$/i.test(s)) s = Number(s).toFixed(0);
    let d = s.replace(/\D/g, '');
    if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
    if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
    return d;
};
const pad = (n) => String(n).padStart(2, '0');
/** Excel dates arrive as Date objects; send them as YYYY-MM-DD. Text and serial numbers go to the server as they are. */
const cellDate = (v) => (v instanceof Date && !Number.isNaN(v.getTime()) ? `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}` : v ?? '');
const shortDate = (v) => {
    if (!v) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) { const [y, m, d] = String(v).split('-'); return `${d}/${m}/${y}`; }
    return String(v);
};

const downloadTemplate = () => {
    const rows = [
        ['Name', 'Mobile', 'Email', 'Gender', 'Birthday', 'Anniversary', 'Address', 'Notes', 'Tags'],
        ['Priya Sharma', '9876543210', 'priya@email.com', 'Female', '07/10/1995', '14/02/2019', 'Sector 18, Noida', 'Likes hair spa', 'VIP'],
        ['Rahul Verma', '9876543211', '', 'Male', '', '', 'Indirapuram', '', ''],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [22, 14, 24, 10, 12, 12, 22, 22, 12].map((wch) => ({ wch }));
    for (let r = 1; r < rows.length; r += 1) { const c = ws[XLSX.utils.encode_cell({ r, c: 1 })]; if (c) { c.t = 's'; c.z = '@'; } }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');
    XLSX.writeFile(wb, 'Hirsta_clients_template.xlsx');
};

const STATUS_LABEL = { ready: 'Ready', problem: 'Problem', repeat: 'Repeated', created: 'Added', updated: 'Updated', skipped: 'Already a client', failed: 'Not imported' };

export default function BulkImportClients({ storeId, canUpdateExisting = false, onClose, onDone }) {
    const fileRef = useRef(null);
    const [step, setStep] = useState('upload');       // upload | review | importing | done
    const [dragging, setDragging] = useState(false);
    const [readError, setReadError] = useState('');
    const [fileName, setFileName] = useState('');
    const [headers, setHeaders] = useState([]);
    const [dataRows, setDataRows] = useState([]);     // [{ rowNo, cells: [] }]
    const [mapping, setMapping] = useState({});
    const [mode, setMode] = useState('skip');
    const [tag, setTag] = useState(() => `Imported ${new Date().toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`);
    const [filter, setFilter] = useState('all');
    const [progress, setProgress] = useState({ done: 0, total: 0, part: 0, parts: 0, retrying: false });
    const [reading, setReading] = useState(false);
    const [resumeFrom, setResumeFrom] = useState(null);   // index in the ready list where an import stopped
    const [results, setResults] = useState(null);     // { summary, byRow: Map }
    const [importError, setImportError] = useState('');

    /* ---------------- read the file ---------------- */
    const readFile = useCallback(async (file) => {
        setReadError('');
        if (!file) return;
        if (!/\.(xlsx|xls|csv)$/i.test(file.name)) { setReadError('Choose an Excel (.xlsx, .xls) or CSV file.'); return; }
        if (file.size > MAX_FILE_MB * 1024 * 1024) { setReadError(`The file is bigger than ${MAX_FILE_MB} MB. Save it as .csv (much smaller) or split it into two files.`); return; }
        setReading(true);
        await wait(30); // let "Reading file…" show before the browser gets busy
        try {
            const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const grid = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '', blankrows: false });
            const headerIdx = grid.slice(0, 10).findIndex((r) => r.filter((c) => String(c).trim()).length >= 2);
            if (headerIdx < 0) { setReadError('No column headings found. The first row should have headings like Name and Mobile.'); return; }
            const head = grid[headerIdx].map((h, i) => String(h).trim() || `Column ${i + 1}`);
            const body = grid.slice(headerIdx + 1)
                .map((cells, i) => ({ rowNo: headerIdx + 2 + i, cells }))
                .filter((r) => r.cells.some((c) => String(c).trim()));
            if (!body.length) { setReadError('The file has headings but no clients under them.'); return; }

            // match columns by their headings
            const auto = {};
            const used = new Set();
            FIELDS.forEach(({ key }) => {
                const idx = head.findIndex((h, i) => !used.has(i) && SYNONYMS[key].includes(normHeader(h)));
                if (idx >= 0) { auto[key] = idx; used.add(idx); }
            });
            if (auto.name !== undefined) { delete auto.firstName; delete auto.lastName; }

            setFileName(file.name); setHeaders(head); setDataRows(body); setMapping(auto); setFilter('all'); setResumeFrom(null); setStep('review');
        } catch (e) {
            setReadError('This file could not be read. Save it again as .xlsx or .csv and try once more.');
        } finally {
            setReading(false);
        }
    }, []);

    /* ---------------- build + check rows ---------------- */
    const rows = useMemo(() => {
        const get = (cells, key) => (mapping[key] === undefined ? '' : cells[mapping[key]]);
        const seen = new Map();
        return dataRows.map(({ rowNo, cells }) => {
            const name = mapping.name !== undefined
                ? String(get(cells, 'name') ?? '').trim()
                : [get(cells, 'firstName'), get(cells, 'lastName')].map((v) => String(v ?? '').trim()).filter(Boolean).join(' ');
            const phoneRaw = get(cells, 'phone');
            const phone = cleanMobile(phoneRaw);
            const row = {
                rowNo, cells,
                payload: {
                    rowNo, name, phone: typeof phoneRaw === 'number' ? Math.round(phoneRaw).toString() : String(phoneRaw ?? ''),
                    email: String(get(cells, 'email') ?? '').trim(), gender: String(get(cells, 'gender') ?? '').trim(),
                    dob: cellDate(get(cells, 'dob')), anniversary: cellDate(get(cells, 'anniversary')),
                    address: String(get(cells, 'address') ?? '').trim(), notes: String(get(cells, 'notes') ?? '').trim(), tags: String(get(cells, 'tags') ?? '').trim(),
                },
                phone,
            };
            if (!name) return { ...row, status: 'problem', reason: 'Name is missing' };
            if (!phone) return { ...row, status: 'problem', reason: 'Mobile is missing' };
            if (phone.length !== 10 || !/^[6-9]/.test(phone)) return { ...row, status: 'problem', reason: `"${row.payload.phone}" is not a valid mobile` };
            if (seen.has(phone)) return { ...row, status: 'repeat', reason: `Same mobile as row ${seen.get(phone)}` };
            seen.set(phone, rowNo);
            return { ...row, status: 'ready', reason: '' };
        });
    }, [dataRows, mapping]);

    const counts = useMemo(() => ({
        ready: rows.filter((r) => r.status === 'ready').length,
        problem: rows.filter((r) => r.status === 'problem').length,
        repeat: rows.filter((r) => r.status === 'repeat').length,
    }), [rows]);
    const nameMapped = mapping.name !== undefined || mapping.firstName !== undefined;
    const phoneMapped = mapping.phone !== undefined;

    /* ---------------- import in parts ---------------- */
    const readyRows = useMemo(() => rows.filter((r) => r.status === 'ready'), [rows]);

    const sendPart = async (part) => {
        let lastError = null;
        for (let attempt = 1; attempt <= RETRIES; attempt += 1) {
            try {
                const res = await fetch(`${SMIIFY_API}/customers/bulk-import`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId },
                    body: JSON.stringify({ rows: part.map((r) => r.payload), mode, tag: tag.trim() }),
                });
                const json = await res.json().catch(() => ({}));
                if (res.ok && json.success) return json.data.results;
                lastError = new Error(json.message || `Import stopped (error ${res.status}).`);
                if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) break;   // a real "no", not a hiccup
            } catch (e) {
                lastError = new Error(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message);
            }
            if (attempt < RETRIES) {
                setProgress((p) => ({ ...p, retrying: true }));
                await wait(1500 * attempt);
                setProgress((p) => ({ ...p, retrying: false }));
            }
        }
        throw lastError;
    };

    /** from = index in readyRows to start at (0 for a fresh import, resumeFrom to continue) */
    const runImport = async (from = 0) => {
        if (!readyRows.length) return;
        const parts = Math.ceil(readyRows.length / CHUNK);
        setStep('importing'); setImportError(''); setResumeFrom(null);
        setProgress({ done: from, total: readyRows.length, part: Math.floor(from / CHUNK) + 1, parts, retrying: false });

        // continuing keeps what the earlier run already saved
        const byRow = from > 0 && results ? new Map(results.byRow) : new Map();
        let stoppedAt = null;
        for (let i = from; i < readyRows.length; i += CHUNK) {
            const part = readyRows.slice(i, i + CHUNK);
            setProgress((p) => ({ ...p, part: Math.floor(i / CHUNK) + 1 }));
            try {
                const res = await sendPart(part);
                res.forEach((x) => byRow.set(x.rowNo, x));
                setProgress((p) => ({ ...p, done: Math.min(i + CHUNK, readyRows.length) }));
            } catch (e) {
                stoppedAt = i;
                setImportError(`${e.message} ${(readyRows.length - i).toLocaleString('en-IN')} clients are not sent yet. Everything before them is saved.`);
                break;
            }
        }

        // rows with problems never leave the screen but belong in the results
        rows.forEach((r) => { if (r.status !== 'ready' && !byRow.has(r.rowNo)) byRow.set(r.rowNo, { rowNo: r.rowNo, status: 'failed', message: r.reason }); });
        const summary = { created: 0, updated: 0, skipped: 0, failed: 0, pending: 0 };
        byRow.forEach((x) => { summary[x.status] = (summary[x.status] || 0) + 1; });
        if (stoppedAt !== null) summary.pending = readyRows.length - stoppedAt;

        setResults({ summary, byRow });
        setResumeFrom(stoppedAt);
        setStep('done');
        if (summary.created || summary.updated) onDone?.();
    };

    const downloadProblems = () => {
        const bad = rows.filter((r) => results.byRow.get(r.rowNo)?.status === 'failed');
        const aoa = [[...headers, 'Problem'], ...bad.map((r) => [...headers.map((_, i) => (r.cells[i] instanceof Date ? cellDate(r.cells[i]) : r.cells[i] ?? '')), results.byRow.get(r.rowNo)?.message || ''])];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), 'Fix these');
        XLSX.writeFile(wb, `Clients_to_fix_${fileName.replace(/\.[^.]+$/, '')}.xlsx`);
    };

    const reset = () => { setStep('upload'); setFileName(''); setHeaders([]); setDataRows([]); setMapping({}); setResults(null); setImportError(''); setResumeFrom(null); };

    /* ---------------- views ---------------- */
    const shown = rows.filter((r) => filter === 'all' || (filter === 'problems' ? r.status !== 'ready' : r.status === 'ready')).slice(0, 200);

    return (
        <div className={styles.overlay} role="presentation" onMouseDown={(e) => e.target === e.currentTarget && step !== 'importing' && onClose()}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Import clients">
                <div className={styles.header}>
                    <div className={styles.headLeft}>
                        <span className={styles.headIcon}><FileSpreadsheet size={18} /></span>
                        <div>
                            <h3>Import clients</h3>
                            <p>{step === 'upload' ? 'From Excel or CSV' : fileName}</p>
                        </div>
                    </div>
                    <ol className={styles.steps} aria-label="Steps">
                        {['Upload', 'Check', 'Import'].map((s, i) => {
                            const at = { upload: 0, review: 1, importing: 2, done: 2 }[step];
                            return <li key={s} className={i < at ? styles.stepDone : i === at ? styles.stepOn : ''}><i>{i < at ? <CheckCircle2 size={12} /> : i + 1}</i>{s}</li>;
                        })}
                    </ol>
                    <button type="button" className={styles.close} onClick={onClose} disabled={step === 'importing'} aria-label="Close"><X size={18} /></button>
                </div>

                <div className={styles.body}>
                    {/* ======================= upload ======================= */}
                    {step === 'upload' && (
                        <>
                            <label
                                className={`${styles.drop} ${dragging ? styles.dropOn : ''}`}
                                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                                onDragLeave={() => setDragging(false)}
                                onDrop={(e) => { e.preventDefault(); setDragging(false); readFile(e.dataTransfer.files?.[0]); }}
                            >
                                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
                                <span className={styles.dropIcon}>{reading ? <Loader2 size={26} className={styles.spin} /> : <UploadCloud size={26} />}</span>
                                <strong>{reading ? 'Reading file…' : 'Drop your file here, or click to choose'}</strong>
                                <span>Excel (.xlsx, .xls) or CSV · any number of clients, sent in parts</span>
                            </label>
                            {readError && <div className={styles.error}><AlertTriangle size={15} /> {readError}</div>}

                            <div className={styles.helpGrid}>
                                <div className={styles.help}>
                                    <strong>What the file needs</strong>
                                    <ul>
                                        <li>A first row with headings, like <b>Name</b> and <b>Mobile</b></li>
                                        <li>One client per row. Mobile can be with +91, 0 or spaces</li>
                                        <li>Optional: Email, Gender, Birthday, Anniversary, Address, Notes, Tags</li>
                                        <li>Dates as 07/10/1995 (day first) or 1995-10-07</li>
                                    </ul>
                                </div>
                                <div className={styles.templateCard}>
                                    <FileSpreadsheet size={22} />
                                    <strong>Start from our template</strong>
                                    <span>Columns already in the right order.</span>
                                    <button type="button" className={styles.ghostBtn} onClick={downloadTemplate}><Download size={14} /> Download template</button>
                                </div>
                            </div>
                        </>
                    )}

                    {/* ======================= review ======================= */}
                    {step === 'review' && (
                        <>
                            <section className={styles.section}>
                                <div className={styles.sectionHead}>
                                    <h4>Match your columns</h4>
                                    <span>{headers.length} columns found. We matched them by their headings; change any that look wrong.</span>
                                </div>
                                <div className={styles.mapGrid}>
                                    {FIELDS.filter((f) => !(mapping.name !== undefined && (f.key === 'firstName' || f.key === 'lastName'))).map((f) => (
                                        <label key={f.key} className={`${styles.mapItem} ${mapping[f.key] !== undefined ? styles.mapSet : ''}`}>
                                            <span>{f.label}{f.required && <em>*</em>}{f.hint && mapping[f.key] === undefined && <small>{f.hint}</small>}</span>
                                            <select value={mapping[f.key] ?? ''} onChange={(e) => setMapping((m) => {
                                                const next = { ...m };
                                                if (e.target.value === '') delete next[f.key]; else next[f.key] = Number(e.target.value);
                                                return next;
                                            })}>
                                                <option value="">Not in file</option>
                                                {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                                            </select>
                                        </label>
                                    ))}
                                </div>
                                {(!nameMapped || !phoneMapped) && (
                                    <div className={styles.error}><AlertTriangle size={15} /> Choose the {!nameMapped && 'Name'}{!nameMapped && !phoneMapped && ' and '}{!phoneMapped && 'Mobile'} column to continue.</div>
                                )}
                            </section>

                            {nameMapped && phoneMapped && (
                                <>
                                    <div className={styles.counts}>
                                        <button type="button" className={`${styles.count} ${styles.countOk} ${filter === 'ready' ? styles.countOn : ''}`} onClick={() => setFilter(filter === 'ready' ? 'all' : 'ready')}>
                                            <CheckCircle2 size={16} /><b>{counts.ready}</b><span>ready to import</span>
                                        </button>
                                        <button type="button" className={`${styles.count} ${styles.countBad} ${filter === 'problems' ? styles.countOn : ''}`} onClick={() => setFilter(filter === 'problems' ? 'all' : 'problems')} disabled={!counts.problem && !counts.repeat}>
                                            <AlertTriangle size={16} /><b>{counts.problem}</b><span>with problems</span>
                                        </button>
                                        <button type="button" className={`${styles.count} ${styles.countWarn} ${filter === 'problems' ? styles.countOn : ''}`} onClick={() => setFilter(filter === 'problems' ? 'all' : 'problems')} disabled={!counts.repeat}>
                                            <Copy size={16} /><b>{counts.repeat}</b><span>repeated in file</span>
                                        </button>
                                    </div>

                                    <div className={styles.tableWrap}>
                                        <table className={styles.table}>
                                            <thead><tr><th>Row</th><th>Name</th><th>Mobile</th><th>Email</th><th>Birthday</th><th>Status</th></tr></thead>
                                            <tbody>
                                                {shown.map((r) => (
                                                    <tr key={r.rowNo} className={r.status !== 'ready' ? styles.rowBad : ''}>
                                                        <td className={styles.muted}>{r.rowNo}</td>
                                                        <td>{r.payload.name || <span className={styles.muted}>—</span>}</td>
                                                        <td className={styles.mono}>{r.phone.length === 10 ? `${r.phone.slice(0, 5)} ${r.phone.slice(5)}` : r.payload.phone || '—'}</td>
                                                        <td>{r.payload.email || <span className={styles.muted}>—</span>}</td>
                                                        <td>{shortDate(r.payload.dob) || <span className={styles.muted}>—</span>}</td>
                                                        <td>
                                                            <span className={`${styles.pill} ${styles[`pill_${r.status}`]}`}>{STATUS_LABEL[r.status]}</span>
                                                            {r.reason && <small className={styles.reason}>{r.reason}</small>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {rows.length > 200 && <p className={styles.more}>Showing 200 of {rows.length} rows. All of them will be imported.</p>}
                                    </div>

                                    <section className={styles.options}>
                                        <div className={styles.option}>
                                            <span className={styles.optLabel}>If the mobile is already a client</span>
                                            <div className={styles.segment}>
                                                <button type="button" className={mode === 'skip' ? styles.segOn : ''} onClick={() => setMode('skip')}><SkipForward size={13} /> Skip them</button>
                                                <button type="button" className={mode === 'update' ? styles.segOn : ''} onClick={() => canUpdateExisting && setMode('update')} disabled={!canUpdateExisting} title={canUpdateExisting ? '' : 'Ask the owner to allow updating existing clients'}>
                                                    <UserCheck size={13} /> Update details
                                                </button>
                                            </div>
                                            <small>{mode === 'update' ? 'Fills in name, email, gender, birthday, address and notes from the file. Wallet, dues and visits never change.' : 'Saved clients stay exactly as they are.'}</small>
                                        </div>
                                        <div className={styles.option}>
                                            <span className={styles.optLabel}><Tag size={12} /> Tag every imported client</span>
                                            <input value={tag} maxLength={40} onChange={(e) => setTag(e.target.value)} placeholder="Leave empty for no tag" />
                                            <small>Helps you find this group later, for example in WhatsApp campaigns.</small>
                                        </div>
                                    </section>
                                </>
                            )}
                        </>
                    )}

                    {/* ======================= importing ======================= */}
                    {step === 'importing' && (
                        <div className={styles.progressBox}>
                            <Loader2 size={30} className={styles.spin} />
                            <strong>Importing clients…</strong>
                            <span>{progress.done.toLocaleString('en-IN')} of {progress.total.toLocaleString('en-IN')} done · part {progress.part} of {progress.parts}</span>
                            <div className={styles.progressTrack}><i style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} /></div>
                            <small className={progress.retrying ? styles.retryNote : styles.keepOpen}>
                                {progress.retrying ? 'Connection slow, trying this part again…' : 'Keep this window open until it finishes.'}
                            </small>
                        </div>
                    )}

                    {/* ======================= done ======================= */}
                    {step === 'done' && results && (
                        <>
                            <div className={styles.doneHead}>
                                <span className={styles.doneIcon}><CheckCircle2 size={26} /></span>
                                <div>
                                    <strong>{resumeFrom !== null ? 'Import paused' : results.summary.created + results.summary.updated > 0 ? 'Import finished' : 'Nothing new was imported'}</strong>
                                    <span>{fileName}</span>
                                </div>
                            </div>
                            {importError && <div className={styles.error}><AlertTriangle size={15} /> {importError}</div>}
                            <div className={styles.resultGrid}>
                                <div className={styles.resAdded}><UserPlus size={16} /><b>{results.summary.created}</b><span>new clients added</span></div>
                                <div className={styles.resUpdated}><UserCheck size={16} /><b>{results.summary.updated}</b><span>existing updated</span></div>
                                <div className={styles.resSkipped}><SkipForward size={16} /><b>{results.summary.skipped}</b><span>already clients, skipped</span></div>
                                <div className={styles.resFailed}><AlertTriangle size={16} /><b>{results.summary.failed}</b><span>not imported</span></div>
                            </div>
                            {resumeFrom !== null && (
                                <div className={styles.resumeBox}>
                                    <div>
                                        <strong>{results.summary.pending.toLocaleString('en-IN')} clients still to send</strong>
                                        <span>The import stopped in the middle. Continue from where it stopped; nothing is sent twice.</span>
                                    </div>
                                    <button type="button" className={styles.primaryBtn} onClick={() => runImport(resumeFrom)}><RefreshCw size={14} /> Continue import</button>
                                </div>
                            )}
                            {results.summary.failed > 0 && (
                                <div className={styles.failBox}>
                                    <div className={styles.failHead}>
                                        <span><Info size={14} /> Fix these rows and import the file again. Already added clients will be skipped.</span>
                                        <button type="button" className={styles.ghostBtn} onClick={downloadProblems}><Download size={14} /> Download rows to fix</button>
                                    </div>
                                    <ul>
                                        {[...results.byRow.values()].filter((x) => x.status === 'failed').slice(0, 30).map((x) => (
                                            <li key={x.rowNo}><b>Row {x.rowNo}</b><span>{x.message}</span></li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className={styles.footer}>
                    {step === 'review' && <button type="button" className={styles.linkBtn} onClick={reset}><RefreshCw size={13} /> Choose another file</button>}
                    {step === 'done' && resumeFrom === null && <button type="button" className={styles.linkBtn} onClick={reset}><RefreshCw size={13} /> Import another file</button>}
                    {step === 'done' && resumeFrom !== null && <span />}
                    {(step === 'upload' || step === 'importing') && <span />}
                    <div className={styles.footRight}>
                        {step !== 'importing' && <button type="button" className={styles.cancelBtn} onClick={onClose}>{step === 'done' ? 'Close' : 'Cancel'}</button>}
                        {step === 'review' && (
                            <button type="button" className={styles.primaryBtn} onClick={() => runImport(0)} disabled={!nameMapped || !phoneMapped || !counts.ready}>
                                Import {counts.ready.toLocaleString('en-IN')} {counts.ready === 1 ? 'client' : 'clients'} <ArrowRight size={14} />
                            </button>
                        )}
                        {step === 'done' && <button type="button" className={styles.primaryBtn} onClick={onClose}><CheckCircle2 size={14} /> Done</button>}
                    </div>
                </div>
            </div>
        </div>
    );
}
